import { CREDENTIAL_FORM_DESCRIPTORS } from "@api/shared-runtime/credential-form-descriptors";
import { CredentialType } from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import type { Credential } from "@/features/credentials/types";
import {
  BLOCKED_UPLOAD_LABEL,
  isReviewUploadBlocked,
} from "../../lib/review-upload-security";
import {
  formatStringList,
  type CredentialFormValues,
} from "@/features/credentials/components/credential-edit-modal/credential-form-values";
import {
  PERSONAL_INFO_FIELD_GROUPS,
  personalInfoFieldKey,
} from "./personal-info-fields";

export type ProviderReviewItem =
  RouterOutputs["credentials"]["listMyCandidates"][number];
type Candidate = NonNullable<ProviderReviewItem["candidate"]>;

function record(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function candidateValues({
  credential,
  candidate,
}: {
  credential: Credential;
  candidate: Candidate;
}): CredentialFormValues {
  if (candidate.credentialType !== credential.credentialType) {
    return {};
  }
  if (candidateHasDifferentIdentity({ credential, candidate })) {
    return {};
  }
  const descriptor = CREDENTIAL_FORM_DESCRIPTORS[credential.credentialType];
  const details = record(candidate.parsed.details);
  const values: CredentialFormValues = {};
  for (const field of descriptor.fields) {
    if (credential.present && field.isIdentity) {
      continue;
    }
    const raw =
      field.source === "column"
        ? candidate.parsed[field.key as keyof Candidate["parsed"]]
        : details[field.key];
    if (raw === null || raw === undefined || raw === "") {
      continue;
    }
    if (field.kind === "boolean") {
      values[`${field.source}.${field.key}`] = raw === true;
    } else if (field.kind === "string-list") {
      values[`${field.source}.${field.key}`] = formatStringList(raw);
    } else {
      values[`${field.source}.${field.key}`] = String(raw);
    }
  }
  if (credential.credentialType === CredentialType.PersonalInfo) {
    for (const group of PERSONAL_INFO_FIELD_GROUPS) {
      const nested = record(details[group.key]);
      for (const field of group.fields) {
        const raw = nested[field.key];
        if (raw === null || raw === undefined || raw === "") {
          continue;
        }
        values[personalInfoFieldKey(group.key, field.key)] =
          field.kind === "string-list" ? formatStringList(raw) : String(raw);
      }
    }
  }
  return values;
}

export function candidateHasDifferentIdentity({
  credential,
  candidate,
}: {
  credential: Credential;
  candidate: Candidate;
}): boolean {
  if (
    !credential.present ||
    candidate.credentialType !== credential.credentialType
  ) {
    return false;
  }
  return CREDENTIAL_FORM_DESCRIPTORS[credential.credentialType].fields.some(
    (field) => {
      if (!field.isIdentity) {
        return false;
      }
      const parsed =
        field.source === "column"
          ? candidate.parsed[field.key as keyof Candidate["parsed"]]
          : record(candidate.parsed.details)[field.key];
      if (parsed === null || parsed === undefined || parsed === "") {
        return false;
      }
      const existing =
        field.source === "column"
          ? credential.version?.[
              field.key as keyof NonNullable<Credential["version"]>
            ]
          : record(credential.version?.details)[field.key];
      return (
        String(parsed).trim().toLowerCase() !==
        String(existing ?? "")
          .trim()
          .toLowerCase()
      );
    },
  );
}

/** First proposed value wins across files; user-touched fields and locked
 * identity are never replaced by a parse that arrives later. */
export function applyProviderReviewProposals({
  credential,
  values,
  blockedKeys,
  items,
}: {
  credential: Credential;
  values: CredentialFormValues;
  blockedKeys: ReadonlySet<string>;
  items: readonly ProviderReviewItem[];
}) {
  const next = { ...values };
  const proposedKeys = new Set<string>();
  for (const item of items) {
    if (
      !item.candidate ||
      item.scanStatus !== "clean" ||
      item.state === "reading"
    ) {
      continue;
    }
    for (const [key, value] of Object.entries(
      candidateValues({ credential, candidate: item.candidate }),
    )) {
      if (blockedKeys.has(key) || proposedKeys.has(key)) {
        continue;
      }
      next[key] = value;
      proposedKeys.add(key);
    }
  }
  const nameKey = "column.number";
  if (
    !credential.present &&
    credential.credentialType === CredentialType.Other &&
    !next[nameKey] &&
    !blockedKeys.has(nameKey) &&
    items[0]
  ) {
    next[nameKey] = items[0].fileName;
    proposedKeys.add(nameKey);
  }
  return { values: next, proposedKeys };
}

/** Parsed nested data has no earlier version while creating. Carry it forward
 * so accepting an address, claims history, or personal-info parse does not
 * discard details the flat form cannot render. Existing records retain their
 * own unsupported details instead of accepting invisible replacements. */
export function credentialWithReviewDefaults({
  credential,
  items,
}: {
  credential: Credential;
  items: readonly ProviderReviewItem[];
}): Credential {
  if (credential.present) {
    return credential;
  }
  const candidate = items.find(
    (item) => item.candidate?.credentialType === credential.credentialType,
  )?.candidate;
  if (!candidate) {
    return credential;
  }
  return {
    ...credential,
    version: {
      id: credential.id,
      number: candidate.parsed.number ?? null,
      stateCode: candidate.parsed.stateCode ?? null,
      issuedAt: candidate.parsed.issuedAt ?? null,
      expiresAt: candidate.parsed.expiresAt ?? null,
      status: candidate.parsed.status ?? null,
      details: candidate.parsed.details ?? {},
    },
  };
}

export function reviewSaveBlocker({
  items,
  selectedIds,
  omittedIds,
  skipParsing = false,
}: {
  items: readonly ProviderReviewItem[];
  selectedIds: ReadonlySet<string>;
  omittedIds: ReadonlySet<string>;
  skipParsing?: boolean;
}): string | null {
  for (const id of selectedIds) {
    if (omittedIds.has(id)) {
      continue;
    }
    const item = items.find((row) => row.documentId === id);
    if (item && isReviewUploadBlocked(item)) {
      return `${BLOCKED_UPLOAD_LABEL}. Remove the blocked file before saving; you can still enter credential details manually.`;
    }
    if (skipParsing && (!item || item.state === "reading")) {
      continue;
    }
    if (!item) {
      return "Wait for the uploaded file to appear, or retry loading it.";
    }
    if (item.state === "reading") {
      return "Wait for the selected files to finish processing, or untick them to discard them.";
    }
    if (item.scanStatus !== "clean") {
      return "A selected file is not ready to attach. Wait for its file check, or untick it to discard it.";
    }
  }
  return null;
}

/** A newly uploaded ID may reach the client before its review row reaches the
 * query cache. Keep polling and lock the draft through that handoff too. */
export function pendingReviewDocumentIds({
  items,
  selectedIds,
  omittedIds,
}: {
  items: readonly ProviderReviewItem[];
  selectedIds: ReadonlySet<string>;
  omittedIds: ReadonlySet<string>;
}): Set<string> {
  return new Set(
    [...selectedIds].filter((id) => {
      if (omittedIds.has(id)) {
        return false;
      }
      const item = items.find((row) => row.documentId === id);
      return (
        !item || (!isReviewUploadBlocked(item) && item.state === "reading")
      );
    }),
  );
}
